// Features: M004-F0764 (room Devices tab), M004-F0765 (Core source-preserving room inventory)
// Tests: T-M004-0344 component contract; Core route contract T-M004-0341
// Core returns Mycelium and Home Assistant sources independently. A source failure is never shown as an empty list.
import React, { useCallback, useEffect, useState } from 'react';
import { roomApi } from '../../services/api';
import './RoomControlsPanel.css';

export const DEVICE_POLL_MS = 10000;
export const DEVICE_LOAD_ERROR = 'Linked devices could not be loaded right now.';
export const DEVICE_PERMISSION_ERROR = 'You do not have permission to view linked devices for this room.';

const SOURCES = [
  { id: 'mycelium', label: 'Mycelium' },
  { id: 'home_assistant', label: 'Home Assistant' },
];
const SOURCE_STATUS = {
  ok: 'Available',
  empty: 'No linked devices',
  not_configured: 'Not configured',
  stale: 'Stale',
  error: 'Unavailable',
};

export const relativeDeviceTime = (value, now = Date.now()) => {
  if (!value) return 'no update time';
  const at = Date.parse(value);
  if (Number.isNaN(at)) return 'no update time';
  const minutes = Math.max(0, Math.round((now - at) / 60000));
  if (minutes < 1) return 'updated just now';
  if (minutes < 60) return `updated ${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `updated ${hours} h ago`;
  return `updated ${Math.round(hours / 24)} d ago`;
};

const payloadOf = (response) => response?.data?.data ?? response?.data ?? {};
const statusOf = (error) => error?.response?.status;
const entityStateText = (entity) => {
  if (entity.state == null || entity.state === '') return 'State unavailable';
  const state = String(entity.state);
  if (state.toLowerCase() === 'unavailable' || state.toLowerCase() === 'unknown') return state[0].toUpperCase() + state.slice(1);
  if (['switch', 'light'].includes(entity.domain) && ['on', 'off'].includes(state.toLowerCase())) {
    return state[0].toUpperCase() + state.slice(1).toLowerCase();
  }
  return `${state}${entity.unit ? ` ${entity.unit}` : ''}`;
};

const DeviceDetails = ({ device }) => {
  if (device.source === 'mycelium') {
    return (
      <>
        <span>{device.type || 'Device'}</span>{' '}
        <span>{device.reported ? (device.observed_status || 'Reported') : 'Not reported by source'}</span>{' '}
        <span className="rc-muted">{relativeDeviceTime(device.last_seen)}</span>
      </>
    );
  }
  return (
    <ul className="rc-sensors" data-testid="room-linked-device-entities">
      {(Array.isArray(device.entities) ? device.entities : []).map((entity) => (
        <li key={`${entity.domain}:${entity.name}`}>
          <span>{entity.name}</span>{' '}
          <span>{entity.domain}</span>{' '}
          <span>{entityStateText(entity)}</span>{' '}
          <span className="rc-muted">{relativeDeviceTime(entity.last_reported || entity.last_changed)}</span>
        </li>
      ))}
    </ul>
  );
};

const RoomLinkedDevicesPanel = ({ roomId }) => {
  const [state, setState] = useState({ status: 'loading' });
  const load = useCallback(async (aliveRef) => {
    try {
      const response = await roomApi.getDevices(roomId);
      if (aliveRef.current) setState({ status: 'ready', data: payloadOf(response) });
    } catch (error) {
      if (aliveRef.current) setState({ status: 'error', message: statusOf(error) === 403 ? DEVICE_PERMISSION_ERROR : DEVICE_LOAD_ERROR });
    }
  }, [roomId]);

  useEffect(() => {
    const alive = { current: true };
    load(alive);
    const timer = setInterval(() => load(alive), DEVICE_POLL_MS);
    return () => { alive.current = false; clearInterval(timer); };
  }, [load]);

  if (state.status === 'loading') return <p className="rc-muted" role="status" data-testid="room-linked-devices-loading">Loading linked devices…</p>;
  if (state.status === 'error') return <p className="rc-reason" role="alert" data-testid="room-linked-devices-error">{state.message}</p>;

  const devices = Array.isArray(state.data?.devices) ? state.data.devices : [];
  return (
    <div className="room-linked-device-sources" data-testid="room-linked-device-sources">
      {SOURCES.map(({ id, label }) => {
        const source = state.data?.sources?.[id] || { status: 'error', last_update_at: null };
        const rows = devices.filter((device) => device.source === id);
        const sourceStatus = SOURCE_STATUS[source.status] || SOURCE_STATUS.error;
        return (
          <section key={id} data-testid={`room-linked-source-${id}`}>
            <h4>{label}</h4>
            <p data-testid={`room-linked-source-status-${id}`} className={source.status === 'error' || source.status === 'stale' ? 'rc-reason' : 'rc-muted'}>
              {sourceStatus}{source.last_update_at ? ` · ${relativeDeviceTime(source.last_update_at)}` : ''}
            </p>
            {source.status === 'error' ? (
              <p className="rc-reason" role="alert">{label} device data is unavailable; this is not an empty inventory.</p>
            ) : rows.length > 0 ? (
              <ul className="rc-sensors" data-testid={`room-linked-list-${id}`}>
                {rows.map((device) => (
                  <li key={device.ref} data-testid="room-linked-device-row">
                    <strong>{device.name}</strong>{' '}
                    <span className="rc-muted">Source: {label}</span>{' '}
                    <DeviceDetails device={device} />
                  </li>
                ))}
              </ul>
            ) : source.status === 'empty' || source.status === 'not_configured' ? (
              <p className="rc-muted" data-testid={`room-linked-empty-${id}`}>
                {source.status === 'not_configured' ? `${label} is not configured for this room.` : `No ${label} devices linked to this room.`}
              </p>
            ) : null}
          </section>
        );
      })}
    </div>
  );
};

export default RoomLinkedDevicesPanel;
